<script setup lang="ts">
import { computed } from 'vue';
import type { RouterItem } from './types';

const props = withDefaults(
  defineProps<{
    item: RouterItem;
    active?: boolean;
    draggable?: boolean;
    isDragOver?: boolean;
  }>(),
  { draggable: true, isDragOver: false },
);

const emit = defineEmits<{
  (event: 'activate', id: string): void;
  (event: 'close', id: string): void;
  (event: 'dragstart', id: string): void;
  (event: 'drop', id: string): void;
  (event: 'dragend'): void;
  (
    event: 'contextmenu',
    payload: { event: MouseEvent; item: RouterItem },
  ): void;
  (event: 'dragoveritem'): void;
  (event: 'dragleaveitem'): void;
}>();

const isDraggable = computed(
  () => props.draggable && props.item.closable !== false,
);

function emitActivate() {
  emit('activate', props.item.id);
}
function emitClose(clickEvent: MouseEvent) {
  clickEvent.stopPropagation();
  emit('close', props.item.id);
}
function emitContext(event: MouseEvent) {
  emit('contextmenu', { event, item: props.item });
}

function handleDragStart(dragEvent: DragEvent) {
  if (!isDraggable.value) {
    dragEvent.preventDefault();
    return;
  }
  dragEvent.dataTransfer?.setData('text/plain', props.item.id);
  dragEvent.dataTransfer?.setDragImage?.(
    dragEvent.currentTarget as HTMLElement,
    10,
    10,
  );
  emit('dragstart', props.item.id);
}
function handleDrop(dragEvent: DragEvent) {
  dragEvent.preventDefault();
  emit('drop', props.item.id);
}
</script>

<template>
  <li
    class="itp-router-item d-flex align-items-center"
    :class="{ active, 'is-drag-over': isDragOver }"
    :draggable="isDraggable"
    @dragstart="handleDragStart"
    @dragover.prevent="emit('dragoveritem')"
    @dragleave="emit('dragleaveitem')"
    @drop="handleDrop"
    @dragend="emit('dragend')"
    @contextmenu.prevent="emitContext"
    @click="emitActivate"
  >
    <slot name="item" :item="item" :active="active">
      <div class="itp-router-item__content">
        <iconify-icon
          v-if="item.icon"
          noobserver
          :icon="item.icon"
          class="me-2"
        />
        <span class="text-truncate">{{ item.title }}</span>
      </div>
    </slot>

    <button
      v-if="item.closable !== false"
      class="btn btn-sm btn-light ms-1 itp-router-item__close"
      type="button"
      aria-label="Close tab"
      title="Close"
      @click.stop="emitClose"
    >
      ✕
    </button>
  </li>
</template>
